import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Snowflake, CalendarDays, Clock, Sparkles, ArrowRight, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { SlotPicker } from "@/components/SlotPicker";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  { key: "murale", label: "Murale", price: 60 },
  { key: "console", label: "Console", price: 70 },
  { key: "cassette", label: "Cassette", price: 90 },
  { key: "gainable", label: "Gainable", price: 110 },
];
const PERFUMES = ["Fraîcheur Marine", "Eucalyptus", "Lavande", "Agrumes", "Coton Frais", "Sans parfum"];

export function QuickBooking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ service_type: "murale", nb_clim: 1, address: "", address_lat: null, address_lon: null, scheduled_date: "", scheduled_time: "", perfume: "Fraîcheur Marine" });
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("ponctuel");
  const set = (k, v) => setForm({ ...form, [k]: v });

  useQuery({ queryKey: ["config"], queryFn: async () => (await api.get("/config/pricing")).data, staleTime: 600000 });

  const unit = TYPES.find((t) => t.key === form.service_type)?.price || 60;
  const price = unit * form.nb_clim;
  const minutes = 45 * form.nb_clim;
  const durText = minutes >= 60 ? `${Math.floor(minutes / 60)}h${minutes % 60 ? (minutes % 60) : ""}` : `${minutes}min`;
  const subDiscount = form.nb_clim >= 6 ? 0.15 : form.nb_clim >= 3 ? 0.10 : 0;
  const monthly = Math.round(20 * form.nb_clim * (1 - subDiscount) * 100) / 100;

  const submit = async () => {
    if (mode === "abonnement") {
      const subPayload = { nb_clim: Number(form.nb_clim), service_type: form.service_type };
      if (user?.role === "client") {
        setBusy(true);
        try {
          await api.post("/subscriptions", subPayload);
          toast.success("Forfait d'entretien activé ! Nous planifions vos visites.");
          navigate("/espace-client");
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
      } else {
        localStorage.setItem("cl_pending_subscription", JSON.stringify(subPayload));
        toast.info("Créez votre compte pour activer votre forfait");
        navigate("/inscription");
      }
      return;
    }
    if (!form.address || !form.scheduled_date) { setStep(2); return toast.error("Indiquez l'adresse et la date"); }
    if (!form.scheduled_time) { setStep(2); return toast.error("Choisissez un créneau horaire"); }
    const payload = { ...form, nb_clim: Number(form.nb_clim), installation_ids: [] };
    if (user?.role === "client") {
      setBusy(true);
      try {
        await api.post("/bookings", payload);
        toast.success("Demande envoyée ! Un technicien va l'accepter.");
        navigate("/espace-client");
      } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    } else {
      localStorage.setItem("cl_pending_booking", JSON.stringify(payload));
      toast.info("Créez votre compte pour confirmer votre réservation");
      navigate("/inscription");
    }
  };

  return (
    <div data-testid="quick-booking" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-blue-900/10 sm:p-7">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-bold text-[#263871]"><Snowflake className="h-5 w-5 text-[#1D9BF0]" />Réserver un nettoyage</h3>
        <div className="flex gap-1">
          {[1, 2, 3].map((s) => <span key={s} className={`h-1.5 w-6 rounded-full ${step >= s ? "bg-[#1D9BF0]" : "bg-slate-200"}`} />)}
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-4 animate-float-up">
          <div>
            <Label className="text-xs text-slate-500">Type de climatisation</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {TYPES.map((t) => (
                <button key={t.key} data-testid={`qb-type-${t.key}`} onClick={() => set("service_type", t.key)} className={`rounded-xl border-2 px-3 py-2.5 text-left text-sm transition-colors ${form.service_type === t.key ? "border-[#1D9BF0] bg-[#E0F2FE]" : "border-slate-200"}`}>
                  <span className="font-semibold text-[#263871]">{t.label}</span><span className="block text-xs text-slate-400">{t.price}€/u</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Nombre de climatiseurs</Label>
            <div className="mt-2 flex items-center gap-4">
              <button data-testid="qb-minus" onClick={() => set("nb_clim", Math.max(1, form.nb_clim - 1))} className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-slate-200 text-[#263871]"><Minus className="h-4 w-4" /></button>
              <span data-testid="qb-nb" className="w-10 text-center text-2xl font-extrabold text-[#263871]">{form.nb_clim}</span>
              <button data-testid="qb-plus" onClick={() => set("nb_clim", form.nb_clim + 1)} className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-slate-200 text-[#263871]"><Plus className="h-4 w-4" /></button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Formule</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button data-testid="qb-mode-ponctuel" onClick={() => setMode("ponctuel")} className={`rounded-xl border-2 p-3 text-left transition-colors ${mode === "ponctuel" ? "border-[#1D9BF0] bg-[#E0F2FE]" : "border-slate-200"}`}>
                <span className="mb-1 block h-[18px]" aria-hidden="true" />
                <span className="block text-sm font-bold text-[#263871]">À la demande</span>
                <span className="block text-xs text-slate-400">{price}€ HT, une fois</span>
              </button>
              <button data-testid="qb-mode-abonnement" onClick={() => setMode("abonnement")} className={`rounded-xl border-2 p-3 text-left transition-colors ${mode === "abonnement" ? "border-[#1D9BF0] bg-[#E0F2FE]" : "border-slate-200"}`}>
                <span className="mb-1 inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Économies</span>
                <span className="block text-sm font-bold text-[#263871]">Abonnement</span>
                <span className="block text-xs text-slate-400">{monthly}€/mois{subDiscount ? ` · −${Math.round(subDiscount * 100)}%` : ""}</span>
              </button>
            </div>
          </div>
          <Estimate mode={mode} price={price} monthly={monthly} durText={durText} subDiscount={subDiscount} />
          <Button data-testid="qb-next-1" onClick={() => setStep(mode === "abonnement" ? 3 : 2)} className="h-12 w-full rounded-full bg-[#263871] hover:bg-[#35508F]">Continuer <ArrowRight className="ml-1 h-4 w-4" /></Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4 animate-float-up">
          <div>
            <Label className="text-xs text-slate-500">Adresse de l'intervention</Label>
            <div className="mt-1"><AddressAutocomplete testid="qb-address" value={form.address} onSelect={({ label, lat, lon }) => setForm({ ...form, address: label, address_lat: lat, address_lon: lon })} /></div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Date</Label>
            <div className="relative mt-1"><CalendarDays className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" /><Input data-testid="qb-date" type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value, scheduled_time: "" })} className="h-12 rounded-xl pl-9" /></div>
          </div>
          <SlotPicker date={form.scheduled_date} nbClim={form.nb_clim} value={form.scheduled_time} onChange={(t) => set("scheduled_time", t)} testid="qb-slots" />
          <Estimate price={price} durText={durText} />
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(1)} className="h-12 flex-1 rounded-full border-2">Retour</Button>
            <Button data-testid="qb-next-2" onClick={() => setStep(3)} className="h-12 flex-1 rounded-full bg-[#263871] hover:bg-[#35508F]">Continuer</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4 animate-float-up">
          <div>
            <Label className="flex items-center gap-1.5 text-xs text-slate-500"><Sparkles className="h-3.5 w-3.5 text-[#1D9BF0]" />Parfum de finition</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {PERFUMES.map((p) => (
                <button key={p} data-testid={`qb-perfume-${p}`} onClick={() => set("perfume", p)} className={`rounded-xl border-2 px-3 py-2.5 text-left text-sm transition-colors ${form.perfume === p ? "border-[#1D9BF0] bg-[#E0F2FE] font-semibold text-[#263871]" : "border-slate-200 text-slate-600"}`}>{p}</button>
              ))}
            </div>
          </div>
          <div className="rounded-xl bg-[#263871] p-4 text-white">
            <div className="flex items-center justify-between text-sm"><span className="text-sky-200">{form.nb_clim} clim · {TYPES.find((t) => t.key === form.service_type)?.label}</span><span className="flex items-center gap-1 text-sky-200"><Clock className="h-3.5 w-3.5" />{durText}</span></div>
            {mode === "abonnement" ? (
              <div className="mt-1 flex items-end justify-between"><span className="text-2xl font-extrabold">{monthly}€ <span className="text-sm font-medium text-sky-200">/mois</span></span><span className="text-xs text-sky-200">Tous les 3 mois · ~{durText}{subDiscount ? ` · −${Math.round(subDiscount * 100)}%` : ""}</span></div>
            ) : (
              <div className="mt-1 flex items-end justify-between"><span className="text-2xl font-extrabold">{price}€ <span className="text-sm font-medium text-sky-200">HT</span></span><span className="text-xs text-sky-200">{form.scheduled_date ? `${form.scheduled_date}${form.scheduled_time ? " · " + form.scheduled_time : ""}` : "Date à définir"}</span></div>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(mode === "abonnement" ? 1 : 2)} className="h-12 flex-1 rounded-full border-2">Retour</Button>
            <Button data-testid="qb-submit" disabled={busy} onClick={submit} className="h-12 flex-1 rounded-full bg-[#1D9BF0] hover:bg-[#57A4FF]">{busy ? "Envoi…" : mode === "abonnement" ? "Activer le forfait" : "Réserver"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Estimate({ mode, price, monthly, durText, subDiscount }) {
  if (mode === "abonnement") {
    return (
      <div className="rounded-xl bg-[#E0F2FE] px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-sm font-medium text-[#263871]"><Clock className="h-4 w-4 text-[#1D9BF0]" />Intervention tous les 3 mois · ~{durText}</span>
          <span className="text-lg font-bold text-[#263871]">{monthly}€<span className="text-xs font-medium text-slate-500">/mois</span></span>
        </div>
        {subDiscount > 0 && <p className="mt-1 text-right text-xs font-semibold text-emerald-600">Remise −{Math.round(subDiscount * 100)}% incluse</p>}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between rounded-xl bg-[#E0F2FE] px-4 py-3">
      <span className="flex items-center gap-1.5 text-sm font-medium text-[#263871]"><Clock className="h-4 w-4 text-[#1D9BF0]" />Durée ~ {durText}</span>
      <span className="text-lg font-bold text-[#263871]">{price}€ HT</span>
    </div>
  );
}
